import { useState, useEffect } from 'react';
import { serviceProvidersApi, ServiceProvider } from '../../api/service-providers.api';
import Table, { Column } from '../../components/common/Table';
import Button from '../../components/forms/Button';
import Loading from '../../components/common/Loading';
import Modal from '../../components/common/Modal';
import Input from '../../components/forms/Input';

const ServiceProviderList = () => {
  const [serviceProviders, setServiceProviders] = useState<ServiceProvider[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState<ServiceProvider | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    contactPerson: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    notes: '',
  });

  useEffect(() => {
    fetchServiceProviders();
  }, []);

  const fetchServiceProviders = async () => {
    try {
      setIsLoading(true);
      const data = await serviceProvidersApi.getAll();
      setServiceProviders(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Servisler yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (provider?: ServiceProvider) => {
    if (provider) {
      setEditingProvider(provider);
      setFormData({
        name: provider.name,
        contactPerson: provider.contactPerson || '',
        phone: provider.phone || '',
        email: provider.email || '',
        address: provider.address || '',
        city: provider.city || '',
        notes: provider.notes || '',
      });
    } else {
      setEditingProvider(null);
      setFormData({
        name: '',
        contactPerson: '',
        phone: '',
        email: '',
        address: '',
        city: '',
        notes: '',
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingProvider(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingProvider) {
        await serviceProvidersApi.update(editingProvider.id, formData);
      } else {
        await serviceProvidersApi.create(formData);
      }
      await fetchServiceProviders();
      handleCloseModal();
    } catch (err: any) {
      setError(err.message || 'İşlem başarısız oldu');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Bu servisi silmek istediğinizden emin misiniz?')) {
      return;
    }
    try {
      await serviceProvidersApi.delete(id);
      await fetchServiceProviders();
    } catch (err: any) {
      setError(err.message || 'Silme işlemi başarısız oldu');
    }
  };

  const columns: Column<ServiceProvider>[] = [
    { key: 'name', header: 'Servis Adı' },
    { key: 'contactPerson', header: 'Yetkili', render: (item) => item.contactPerson || '-' },
    { key: 'phone', header: 'Telefon', render: (item) => item.phone || '-' },
    { key: 'city', header: 'Şehir', render: (item) => item.city || '-' },
    {
      key: 'isActive',
      header: 'Durum',
      render: (item) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-semibold ${
            item.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {item.isActive ? 'Aktif' : 'Pasif'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'İşlemler',
      render: (item) => (
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => handleOpenModal(item)}>
            Düzenle
          </Button>
          <Button size="sm" variant="danger" onClick={() => handleDelete(item.id)}>
            Sil
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <Loading text="Servisler yükleniyor..." />;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Onarım Servisleri</h1>
        <Button onClick={() => handleOpenModal()}>Yeni Servis Ekle</Button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
          {error}
        </div>
      )}

      <Table data={serviceProviders} columns={columns} emptyMessage="Henüz servis bulunmamaktadır" />

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingProvider ? 'Servis Düzenle' : 'Yeni Servis Ekle'}
      >
        <form onSubmit={handleSubmit}>
          <Input
            label="Servis Adı"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <Input
            label="Yetkili Kişi"
            value={formData.contactPerson}
            onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
          />
          <Input
            label="Telefon"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          />
          <Input
            label="E-posta"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
          />
          <Input
            label="Adres"
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
          />
          <Input
            label="Şehir"
            value={formData.city}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
          />
          <Input
            label="Notlar"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          />
          <div className="flex gap-2 mt-6">
            <Button type="submit" fullWidth>
              {editingProvider ? 'Güncelle' : 'Ekle'}
            </Button>
            <Button type="button" variant="outline" fullWidth onClick={handleCloseModal}>
              İptal
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ServiceProviderList;
